import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useBilling } from "../hooks/useBilling";
import { ViewIcon, PayrollIcon } from "../Icons";
import { displayDateUS } from "../utils/dateUtils";
import toast from "react-hot-toast";
import Table from "../components/UI/Table";
import { PAGE_SHELL, PAGE_HEADER_ROW, PAGE_SUBTITLE } from "../ui/pageChrome";
import {
  SUBSCRIPTION_BTN,
  SUBSCRIPTION_COL,
  SUBSCRIPTION_PAGE,
  SUBSCRIPTION_PILL,
  SUBSCRIPTION_THEME,
  subscriptionStatus,
} from "./subscriptionTheme";

const CELL = "text-[13px] text-slate-700 leading-none";
const CELL_STRONG = "text-[13px] font-medium text-slate-900 leading-none";

const ManageSubscriptions = () => {
  const navigate = useNavigate();
  const { fetchBills } = useBilling();
  const [bills, setBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);

  const loadBills = useCallback(async () => {
    setLoadingBills(true);
    const result = await fetchBills();
    if (result.success) {
      setBills(result.data);
    } else {
      toast.error("Failed to load bills");
    }
    setLoadingBills(false);
  }, [fetchBills]);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const formatDate = (date) => displayDateUS(date) || "—";
  const formatAmount = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

  const handleViewDetails = (billId) => {
    navigate(`/bills/${billId}`);
  };

  const columns = [
    {
      header: "Bill Number",
      accessor: "billNumber",
      className: SUBSCRIPTION_COL.billNumber,
      render: (row) => (
        <span className={CELL_STRONG}>{row.billNumber || "—"}</span>
      ),
    },
    {
      header: "Billing Period",
      accessor: "billingPeriodStart",
      className: SUBSCRIPTION_COL.period,
      render: (row) => (
        <span className={`${CELL} tabular-nums`}>
          {formatDate(row.billingPeriodStart)} – {formatDate(row.billingPeriodEnd)}
        </span>
      ),
    },
    {
      header: "Usage",
      accessor: "usageSummary",
      className: "min-w-[180px]",
      render: (row) => {
        const summary = row.usageSummary || {};
        const mode = summary.chargeMode || "EMPLOYEES";
        const sections = Array.isArray(summary.chargeSections)
          ? summary.chargeSections
          : null;
        if (sections && sections.length > 0) {
          return (
            <div className="space-y-1">
              {sections.map((section) => (
                <span key={section.key} className={`block ${CELL} text-slate-600`}>
                  {section.title}: {section.actual ?? 0} billable
                  {section.extra > 0 ? ` · ${section.extra} extra` : ""}
                </span>
              ))}
            </div>
          );
        }
        const bits = [];
        if (mode === "EMPLOYEES" || mode === "BOTH") {
          bits.push(`${summary.actualUsers ?? 0} employees`);
        }
        if (mode === "PATIENTS" || mode === "BOTH") {
          bits.push(`${summary.actualPatients ?? 0} patients`);
        }
        return (
          <span className={`${CELL} text-slate-600`}>
            {bits.join(" · ") || "—"}
          </span>
        );
      },
    },
    {
      header: "Amount",
      accessor: "totalAmount",
      className: SUBSCRIPTION_COL.amount,
      render: (row) => (
        <div className="leading-none">
          <span className="text-[13px] font-semibold text-slate-900 tabular-nums">
            {formatAmount(row.totalAmount)}
          </span>
          {(parseFloat(row.paidAmount) || 0) > 0 && row.status === "PARTIAL" && (
            <span className="block text-[11px] text-slate-500 mt-1 tabular-nums">
              Paid: {formatAmount(row.paidAmount)}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Due Date",
      accessor: "dueDate",
      className: SUBSCRIPTION_COL.dueDate,
      render: (row) => (
        <span className={`${CELL} tabular-nums`}>{formatDate(row.dueDate)}</span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      className: SUBSCRIPTION_COL.status,
      render: (row) => {
        const tone = subscriptionStatus(row.status);
        return (
          <span className={`${SUBSCRIPTION_PILL} ${tone.className}`}>
            {tone.label}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessor: "actions",
      className: SUBSCRIPTION_COL.actions,
      render: (row) => (
        <button
          type="button"
          onClick={() => handleViewDetails(row.id)}
          className={SUBSCRIPTION_BTN.row}
        >
          <ViewIcon size={13} />
          View
        </button>
      ),
    },
  ];

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={SUBSCRIPTION_PAGE.title}>Manage Billing</h1>
          <p className={PAGE_SUBTITLE}>
            View and manage your HRMS billing and invoices
          </p>
        </div>
      </div>

      <div className={SUBSCRIPTION_PAGE.card}>
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${SUBSCRIPTION_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${SUBSCRIPTION_THEME.section.bar}`}
            aria-hidden
          />
          <span className={`shrink-0 ${SUBSCRIPTION_THEME.section.icon}`}>
            <PayrollIcon size={14} />
          </span>
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${SUBSCRIPTION_THEME.section.title}`}
          >
            Billing History
          </h2>
          {bills.length > 0 && (
            <span
              className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${SUBSCRIPTION_THEME.section.badge}`}
            >
              {bills.length}
            </span>
          )}
        </div>

        <Table
          columns={columns}
          data={bills}
          loading={loadingBills}
          showSrNo
          density="compact"
          embedded
          headTone={SUBSCRIPTION_THEME.tableHead}
          noDataMessage="No bills yet. Your billing history will appear here."
        />
      </div>
    </div>
  );
};

export default ManageSubscriptions;
